import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Clock } from "lucide-react";
import { fromGeminiAspectRatio, resolveWatermarkStyle } from "@fiket/booth-schema";
import { useRouter } from "../../store/router";
import { useI18n } from "../../i18n/useI18n";
import { useSelectedStyle, useOutputAspect, useOutputPlan, styleName, styleStory } from "../../hooks/useStyles";
import { cropDataUrlToAspect } from "../../utils/cropImage";
import { Button } from "../../components/ui/Button";
import { Lines } from "../../components/ui/Lines";
import { RingSpinner } from "../../components/ui/RingSpinner";
import { HairlineProgress } from "../../components/ui/HairlineProgress";
import { useTransformJob } from "./useTransformJob";
import { useBoothWatermark } from "../../store/booth";
import { getWatermarkImage } from "../../utils/watermarkImage";

const STEP_ROTATE_MS = 8000;
const ESTIMATED_TOTAL_MS = 30_000;
const QUEUE_WAIT_MS = 30_000;   // 30s+ → 대기열 안내
const TOOK_TOO_LONG_MS = 60_000; // 60s+ → 취소 옵션
/**
 * 변환 입력 긴 변 상한(px). 카메라가 4:3 최대 해상도(iPad 12MP = 3024×4032) 를 주면 그대로 보낼 때
 * base64 본문이 Lambda 6MB 한도에 근접한다. Gemini 결과는 활성 프리셋 px(기본 1350×1800) 로 나오므로
 * 2048 이면 입력 정보가 충분하다.
 */
const TRANSFORM_INPUT_MAX_SIDE = 2048;

/**
 * STAGE · Generate (HERIT 04a · BOOTH-54) — 사진 대신 이야기를 보여주며 변환을 기다린다.
 * 상단 스타일 칩(썸네일 · SELECTED STYLE · 이름) / 남은 시간, 중앙 링 스피너 + 스타일 제목 + 헤어라인 + 설명,
 * 하단 "DEVELOPING · n / 100" 진행 트랙. 변환 job 상태를 store 의 shot 에 동기화하고, 완료되면 review 로.
 */
export function GenerateView() {
  const go = useRouter((s) => s.go);
  const shots = useRouter((s) => s.capturedShots);
  const styleId = useRouter((s) => s.selectedStyleId);
  const setTransformed = useRouter((s) => s.setTransformed);
  const clearShots = useRouter((s) => s.clearShots);
  const { t, language } = useI18n();
  const style = useSelectedStyle();
  const aspect = useOutputAspect();
  const plan = useOutputPlan();
  // 변환 전 크롭은 Gemini 가 실제로 받는 enum 비율(예: 3:2)로 — 요청 비율과 입력 비율을 일치시킨다.
  // 인화지 프레임(aspect)은 프리셋 비율(1.48 등) 그대로. 그 차이는 결과 수신 후 fitTo 가 잘라낸다 (BOOTH-35).
  const cropAspect = useMemo(
    () => (plan ? fromGeminiAspectRatio(plan.geminiAspectRatio) : aspect),
    [plan, aspect]
  );
  const fitTo = useMemo(
    () => (plan ? { w: plan.targetPx.w, h: plan.targetPx.h, dpi: plan.preset.dpi } : null),
    [plan]
  );

  const STEP_TEXTS = [t.generate.step0, t.generate.step1, t.generate.step2, t.generate.step3];
  const [stepIndex, setStepIndex] = useState(0);
  const [elapsedMs, setElapsedMs] = useState(0);

  // TOTAL_SHOTS=1 전제 — 첫 shot 만 변환 대상.
  const shot = shots[0] ?? null;
  const [croppedImage, setCroppedImage] = useState<string | null>(null);

  // 워터마크 이미지 로드 (BOOTH-103). `useBoothConfig.load` 에서 이미 프리로드하므로 보통 즉시 반환된다.
  const wm = useBoothWatermark();
  // 위치·크기 (BOOTH-129) — 옛 저장값(style 없음)은 기본으로 해석
  const wmStyle = useMemo(() => resolveWatermarkStyle(wm), [wm]);
  const [wmImage, setWmImage] = useState<HTMLImageElement | null>(null);
  useEffect(() => {
    let cancelled = false;
    void getWatermarkImage(wm?.imageUrl ?? null).then((img) => {
      if (!cancelled) setWmImage(img);
    });
    return () => { cancelled = true; };
  }, [wm?.imageUrl]);

  // 되돌림은 아직 이 화면일 때만 — 처음으로(resetSession) 뒤 퇴장 애니메이션 중 style·shot 이 비면서 attract 를 덮어쓰던 회귀(2026-10-21)
  const route = useRouter((s) => s.route);
  useEffect(() => {
    if (route !== "generate") return;
    if (!styleId) {
      go("styleSelect");
      return;
    }
    if (!shot) {
      go("preview");
      return;
    }
    let cancelled = false;
    (async () => {
      const cropped = await cropDataUrlToAspect(shot.dataUrl, cropAspect, {
        maxSide: TRANSFORM_INPUT_MAX_SIDE,
      });
      if (!cancelled) setCroppedImage(cropped);
    })();
    return () => {
      cancelled = true;
    };
  }, [route, shot, styleId, cropAspect, go]);

  const job = useTransformJob({ image: croppedImage, styleId, fitTo, watermark: wmImage, watermarkStyle: wmStyle });

  // job 상태 → store shot 동기화. 각 분기에 idempotent 가드(무한 루프 방지).
  useEffect(() => {
    if (!shot) return;
    if (job.status === "queued" || job.status === "running") {
      if (shot.transformStatus !== "generating") setTransformed(shot.id, null, "generating");
    } else if (job.status === "done" && job.resultDataUrl) {
      // videoInputDataUrl: 워터마크 없는 사본 — 영상 생성 입력으로 쓴다 (BOOTH-103).
      if (shot.transformStatus !== "done") setTransformed(shot.id, job.resultDataUrl, "done", job.videoInputDataUrl);
    } else if (job.status === "error") {
      if (shot.transformStatus !== "error") setTransformed(shot.id, null, "error");
    }
  }, [job.status, job.resultDataUrl, job.videoInputDataUrl, shot, setTransformed]);

  useEffect(() => {
    const startedAt = Date.now();
    const tick = setInterval(() => setElapsedMs(Date.now() - startedAt), 500);
    const stepTimer = setInterval(
      () => setStepIndex((i) => Math.min(i + 1, STEP_TEXTS.length - 1)),
      STEP_ROTATE_MS
    );
    return () => {
      clearInterval(tick);
      clearInterval(stepTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (shots.length === 0) return;
    if (shots.every((s) => s.transformStatus === "done")) {
      const to = setTimeout(() => go("review"), 500);
      return () => clearTimeout(to);
    }
    return undefined;
  }, [shots, go]);

  const hasError = shots.some((s) => s.transformStatus === "error");
  const anyGenerating = shots.some((s) => s.transformStatus === "generating");
  const remainingSec = Math.max(0, Math.round((ESTIMATED_TOTAL_MS - elapsedMs) / 1000));
  const isOvertime = elapsedMs > ESTIMATED_TOTAL_MS;
  const queueWait = elapsedMs > QUEUE_WAIT_MS && !hasError;
  const tookTooLong = elapsedMs > TOOK_TOO_LONG_MS && !hasError;

  const back = () => {
    clearShots();
    go("preview");
  };

  const status = hasError
    ? t.generate.failed
    : tookTooLong
      ? t.generate.tookTooLong
      : queueWait
        ? t.generate.queueWait
        : isOvertime
          ? t.generate.almostDone
          : t.generate.remaining(remainingSec);
  // 진행률 — 예상 시간 대비, 완료 전엔 99 에서 멈춘다(실제 done 은 review 전환으로 드러난다)
  const allDone = shots.length > 0 && shots.every((s) => s.transformStatus === "done");
  const pct = allDone ? 100 : Math.min(99, Math.round((elapsedMs / ESTIMATED_TOTAL_MS) * 100));
  const name = styleName(style, language);
  // 스타일별 소개 문구(story*) — 콘솔 "생성 화면 소개". 해당 언어 값이 없으면 "" → 아래에서 단락을 렌더하지 않음(폴백 없음).
  const story = styleStory(style, language);

  return (
    <div className="view view--generate" aria-live="polite">
      <header className="style-chip">
        <div className="style-chip__who">
          {style?.thumbnailUrl ? <img src={style.thumbnailUrl} alt="" draggable={false} /> : <span className="style-chip__blank" />}
          <div>
            <span className="kicker">{t.generate.selectedLabel}</span>
            <strong>{name}</strong>
          </div>
        </div>
        <span className="eta" style={hasError ? { color: "var(--color-error)" } : undefined}>
          <Clock size={16} aria-hidden />
          {elapsedMs < 1000 ? t.generate.hint : status}
        </span>
      </header>

      <div className="view__body center generate__body">
        <RingSpinner progress={hasError ? 0 : pct / 100} />
        <h2 className="story__title">{name}</h2>
        <i className="divider" aria-hidden />
        {story ? <p className="story"><Lines text={story} /></p> : null}
        {/* 단계 문구(얼굴 살펴보기 → 의상 → 배경 → 마무리) — 오류 시 오류 메시지 */}
        <div className="story__step">
          <AnimatePresence mode="wait">
            <motion.span
              key={hasError ? "err" : stepIndex}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.3 }}
              style={{ color: hasError ? "var(--color-error)" : "var(--color-muted)" }}
            >
              {hasError ? job.errorMessage ?? t.generate.failed : STEP_TEXTS[stepIndex]}
            </motion.span>
          </AnimatePresence>
        </div>
        <div className="generate__controls">
          {hasError && !anyGenerating ? (
            <>
              <Button variant="secondary" onClick={() => job.restart()}>{t.generate.retry}</Button>
              <Button onClick={() => go("review")}>{t.generate.proceed}</Button>
            </>
          ) : tookTooLong ? (
            <Button variant="secondary" onClick={back}>{t.generate.cancel}</Button>
          ) : (
            <Button variant="text" onClick={back}>{t.generate.back}</Button>
          )}
        </div>
      </div>

      <HairlineProgress className="generate__progress" value={pct} label={`${t.generate.developing} · ${pct} / 100`} />
    </div>
  );
}
